// HUD 면적 예산(C8-8, 비평가 P1 "폰 전투 HUD가 화면을 많이 먹는다"): 사각형들의 합집합 넓이를 화면 넓이로 나눈 점유율.
// 좌표 압축으로 정확히 잰다(겹친 칸을 두 번 세지 않는다). 순수 함수 — 시험과 QA 촬영 도구(raid-frame-shots)가 같은 식으로 잰다.
// 레이아웃이 바뀔 때·시험에서만 부르고 프레임 루프에서는 부르지 않는다.
import type { HudRect } from './skillHudLayout';

/** rects 합집합 중 clip 안의 넓이(px²) */
export function unionArea(rects: readonly HudRect[], clip: HudRect): number {
  const cx0 = clip.x, cy0 = clip.y, cx1 = clip.x + clip.w, cy1 = clip.y + clip.h;
  const rs: [number, number, number, number][] = [];
  for (const r of rects) {
    const x0 = Math.max(cx0, r.x), y0 = Math.max(cy0, r.y), x1 = Math.min(cx1, r.x + r.w), y1 = Math.min(cy1, r.y + r.h);
    if (x1 > x0 && y1 > y0) rs.push([x0, y0, x1, y1]);
  }
  if (!rs.length) return 0;
  const xs = [...new Set(rs.flatMap((r) => [r[0], r[2]]))].sort((a, b) => a - b);
  let area = 0;
  for (let i = 0; i + 1 < xs.length; i++) {
    const a = xs[i], b = xs[i + 1];
    const spans = rs.filter((r) => r[0] <= a && r[2] >= b).map((r) => [r[1], r[3]] as const).sort((p, q) => p[0] - q[0]);
    let cover = 0, s = -Infinity, e = -Infinity;
    for (const [y0, y1] of spans) {
      if (y0 > e) { if (e > s) cover += e - s; s = y0; e = y1; } else if (y1 > e) e = y1;
    }
    if (e > s) cover += e - s;
    area += cover * (b - a);
  }
  return area;
}

/** 화면 점유율(0..1): rects 합집합 / 화면 넓이 */
export function hudCoverage(rects: readonly HudRect[], w: number, h: number): number {
  return w > 0 && h > 0 ? unionArea(rects, { x: 0, y: 0, w, h }) / (w * h) : 0;
}

/** 원(둥근 버튼)은 실제로 덮는 넓이가 바깥 사각형의 π/4다. 원 칸 목록의 넓이 합(서로 겹치지 않는 배치 전제: 버튼 사이 간격 ≥ 0) */
export function circleArea(rects: readonly HudRect[]): number {
  let a = 0;
  for (const r of rects) a += Math.PI / 4 * r.w * r.h;
  return a;
}

/**
 * 전투 영역: 화면 가장자리 띠를 뺀 안쪽(좌우 1/6씩, 위 20%를 뺀 나머지). 주인공·보스·아군·텔레그래프가 서는 곳이라
 * 여기를 덮는 HUD가 체감 면적이다(가장자리 HUD는 적게 가린다). 아래는 빼지 않는다 — 아군이 화면 아래쪽에 선다.
 */
export function battleZone(w: number, h: number): HudRect {
  return { x: w / 6, y: h * 0.2, w: (w * 2) / 3, h: h * 0.8 };
}
/** 전투 영역 점유율(0..1) */
export function battleCoverage(rects: readonly HudRect[], w: number, h: number): number {
  const z = battleZone(w, h);
  return unionArea(rects, z) / (z.w * z.h);
}
